/**
 * @fileoverview Hook de Actions Layer para gestión de selección y lectura de conversaciones en el inbox.
 * Implementa el patrón de **Actions Layer** con separación estricta de responsabilidades, enfocándose en operaciones de negocio sobre la lista de chats.
 * Optimizado para performance, memoización y manejo robusto de errores.
 * Cumple arquitectura Mercately: separación de capas, performance y documentación avanzada.
 * @module useInboxChatListActions
 * @category Hook - Actions Layer
 * @since 2.0.0
 * @version 2.2.0 - JSDoc completo y actualizado
 */

import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSelectedConversation,
  setChatReadToggleState,
  SET_CONVERSATION_MESSAGES_DATA
} from '../../../../actions/inbox/conversationActions';
import useAreaChatWindowMessagesLoader from '../data/useAreaChatWindowMessagesLoader';
import { sharingInformationService } from '../../../../services/sharingInformationService';

/**
 * Hook Actions Layer para gestión de selección y lectura de conversaciones en el inbox.
 *
 * Implementa el patrón de **Actions Layer**:
 * - Solo operaciones de negocio sobre la lista de chats
 * - No mezcla lógica de UI ni de datos
 * - Optimizado con useCallback y separación estricta
 *
 * **Responsabilidades:**
 * 1. Seleccionar conversación y actualizar el estado global
 * 2. Vaciar mensajes previos al cambiar de conversación
 * 3. Cargar mensajes de la conversación seleccionada (página 1)
 * 4. Marcar como leída la conversación si corresponde
 */
export default function useInboxChatListActions() {
  const dispatch = useDispatch();

  const loading = useSelector((state) => state.conversationMessagesReducer.loading);
  // Nuevo hook para cargar mensajes por página
  const loadMessages = useAreaChatWindowMessagesLoader();

  /**
   * Acción principal para seleccionar y preparar una conversación en el inbox.
   *
   * **Flujo de operación:**
   * 1. Selecciona la conversación en el estado global (acción síncrona)
   * 2. Vacía los mensajes previos para evitar inconsistencias
   * 3. Carga los mensajes de la conversación seleccionada (página 1)
   * 4. Marca la conversación como leída si corresponde
   */
  const handleSelectConversation = useCallback((conversation) => {
    if (loading) return; // Evitar acciones concurrentes si ya se está cargando

    // Disparar envío de consolidación pendiente del chat actual antes de cambiar la selección
    sharingInformationService.setSubject({ flushConsolidation: true });
    sharingInformationService.setSubject({});

    // 1. Operación síncrona prioritaria - selección inmediata de conversación
    dispatch(setSelectedConversation(conversation.id));

    // 2. Vaciar el listado de chats al cambiar de conversación
    dispatch({
      type: SET_CONVERSATION_MESSAGES_DATA,
      payload: { data: [] }
    });

    // 3. Operación asíncrona secundaria - marcar como leída si es necesario
    if (conversation.isUnRead) {
      dispatch(setChatReadToggleState([{
        id: conversation.id,
        platform: conversation.platform
      }]));
    }

    // 4. Operación asíncrona secundaria - cargar mensajes de la conversación
    loadMessages(conversation, 1);

    // 5. Operación asíncrona secundaria - focus textarea
    sharingInformationService.setSubject({ focusInboxChatTextarea: true });
    sharingInformationService.setSubject({});
  }, [dispatch, loadMessages, loading]);

  useEffect(() => {
    const handler = (event) => {
      const { conversationId, platform, customerId } = event.detail;
      handleSelectConversation({
        id: conversationId,
        platform,
        customerId
      });
    };

    // Limpieza opcional al desmontar el componente
    window.addEventListener("inbox:open-conversation", handler);

    return () => {
      window.removeEventListener("inbox:open-conversation", handler);
    };
  }, [handleSelectConversation]);

  return { handleSelectConversation };
}
